'use client';

import React, { createContext, useContext, useRef, type ReactNode } from 'react';
import { useStore, type StoreApi } from 'zustand';
import { createBoardStore, type BoardState } from '../../store/useBoardStore';
import type { BoardCardData, BoardColumn, FetchFunction, OnCardMove, OnMoveError } from '../../types/board.types';

export type BoardAppearance = 'legacy' | 'v2';

interface BoardContextValue<T extends BoardCardData = BoardCardData> {
    store: StoreApi<BoardState<T>>;
    fetchFn: FetchFunction<T>;
    onCardMove?: OnCardMove;
    onMoveError?: OnMoveError;
    onCardClick?: (card: T, columnId: string) => void;
    renderCard: (card: T, columnId: string) => ReactNode;
    renderColumnExtra?: (column: BoardColumn) => ReactNode;
    appearance: BoardAppearance;
}

const BoardContext = createContext<BoardContextValue | null>(null);

export interface BoardProviderProps<T extends BoardCardData = BoardCardData> {
    fetchFunction: FetchFunction<T>;
    onCardMove?: OnCardMove;
    onMoveError?: OnMoveError;
    onCardClick?: (card: T, columnId: string) => void;
    renderCard: (card: T, columnId: string) => ReactNode;
    renderColumnExtra?: (column: BoardColumn) => ReactNode;
    /**
     * Visual appearance of the shared kanban / data view primitives.
     * - `legacy` (default): original indigo/gray palette used across v1 and
     *   existing callers like `/my-reviews/[assessmentId]`.
     * - `v2`: Lantern design-system palette (cream surfaces, coral accent,
     *   token-driven via `src/styles/design-tokens-v2.css`).
     *
     * This flag is threaded through context so nested components
     * (`KanbanColumn`, `ColumnSortFilter`, sub-column drop zones, etc.) can
     * render tokens without prop-drilling.
     */
    appearance?: BoardAppearance;
    children: ReactNode;
}

export function BoardProvider<T extends BoardCardData = BoardCardData>({
    fetchFunction,
    onCardMove,
    onMoveError,
    onCardClick,
    renderCard,
    renderColumnExtra,
    appearance = 'legacy',
    children,
}: BoardProviderProps<T>) {
    const storeRef = useRef<StoreApi<BoardState<T>>>();
    if (!storeRef.current) {
        storeRef.current = createBoardStore<T>();
    }

    const value: BoardContextValue<T> = {
        store: storeRef.current,
        fetchFn: fetchFunction,
        onCardMove,
        onMoveError,
        onCardClick: onCardClick as BoardContextValue['onCardClick'],
        renderCard: renderCard as BoardContextValue['renderCard'],
        renderColumnExtra: renderColumnExtra as BoardContextValue['renderColumnExtra'],
        appearance,
    };

    return (
        <BoardContext.Provider value={value as unknown as BoardContextValue}>
            {children}
        </BoardContext.Provider>
    );
}

export function useBoardContext<T extends BoardCardData = BoardCardData>() {
    const ctx = useContext(BoardContext);
    if (!ctx) throw new Error('useBoardContext must be used within a BoardProvider');
    return ctx as unknown as BoardContextValue<T>;
}

export function useBoardStore<T extends BoardCardData, R>(
    selector: (state: BoardState<T>) => R
): R {
    const { store } = useBoardContext<T>();
    return useStore(store, selector as (state: BoardState<T>) => R);
}

/**
 * Read the current board appearance (`'legacy' | 'v2'`). Use from any
 * component rendered under `<BoardProvider>` to branch token classes without
 * prop-drilling.
 */
export function useBoardAppearance(): BoardAppearance {
    const ctx = useContext(BoardContext);
    return ctx?.appearance ?? 'legacy';
}
